import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Users,
  Settings,
  BarChart2,
  UserPlus,
  ChevronRight,
  Trophy,
  ClipboardList,
  Utensils,
  Heart,
  Pill,
} from "lucide-react";
import { AttendanceModule } from "./AttendanceModule";
import { CantineModule } from "./CantineModule";
import { HealthModule } from "./HealthModule";
import { MedicationsModule } from "./MedicationsModule";
import { listStudents, getCantineSummary } from "../../lib/api";

const CLASS_SECTIONS = [
  {
    id: "students",
    title: "Mes élèves",
    description: "Gérer les profils et codes d'accès",
    icon: Users,
    color: "bg-blue-500",
    lightColor: "bg-blue-50 hover:bg-blue-100 border-blue-200",
    path: "/students",
  },
  {
    id: "attendance",
    title: "Appel",
    description: "Appel matin et après-midi",
    icon: ClipboardList,
    color: "bg-indigo-500",
    lightColor: "bg-indigo-50 hover:bg-indigo-100 border-indigo-200",
    isDialog: true,
  },
  {
    id: "profiles",
    title: "Profils de lecture",
    description: "Configurer les adaptations",
    icon: Settings,
    color: "bg-purple-500",
    lightColor: "bg-purple-50 hover:bg-purple-100 border-purple-200",
    path: "/profiles",
  },
  {
    id: "dashboard",
    title: "Vue classe",
    description: "Tableau de bord et statistiques",
    icon: BarChart2,
    color: "bg-green-500",
    lightColor: "bg-green-50 hover:bg-green-100 border-green-200",
    path: "/class",
  },
];

const REGISTRY_SECTIONS = [
  {
    id: "cantine",
    title: "Cantine",
    description: "Liste et régimes",
    icon: Utensils,
    color: "bg-amber-500",
    lightColor: "bg-amber-50 hover:bg-amber-100 border-amber-200",
    isDialog: true,
  },
  {
    id: "health",
    title: "Santé / Allergies",
    description: "PAI et allergies",
    icon: Heart,
    color: "bg-rose-500",
    lightColor: "bg-rose-50 hover:bg-rose-100 border-rose-200",
    isDialog: true,
  },
  {
    id: "medications",
    title: "Médicaments",
    description: "Sorties scolaires",
    icon: Pill,
    color: "bg-teal-500",
    lightColor: "bg-teal-50 hover:bg-teal-100 border-teal-200",
    isDialog: true,
  },
  {
    id: "progress",
    title: "Progression",
    description: "Progrès élèves",
    icon: Trophy,
    color: "bg-orange-500",
    lightColor: "bg-orange-50 hover:bg-orange-100 border-orange-200",
    path: "/class",
  },
];

// Hook pour détecter le mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

export function TeacherClassManagementTab() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // États des modaux
  const [showAttendance, setShowAttendance] = useState(false);
  const [showCantine, setShowCantine] = useState(false);
  const [showHealth, setShowHealth] = useState(false);
  const [showMedications, setShowMedications] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalStudents: 0,
    cantineCount: 0,
    paiCount: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const studentsRes = await listStudents();
        const students = studentsRes.data || [];
        
        setStats({
          totalStudents: students.length,
          cantineCount: students.filter(s => s.cantine_info?.eats_at_cantine).length,
          paiCount: students.filter(s => s.health_info?.pai || s.health_info?.allergies?.length > 0).length,
        });
      } catch (err) {
        console.error("Erreur chargement stats:", err);
      }
    };
    fetchStats();
  }, [showAttendance, showCantine, showHealth, showMedications]);

  const handleSectionClick = (section) => {
    if (section.isDialog) {
      switch (section.id) {
        case "attendance":
          setShowAttendance(true);
          break;
        case "cantine":
          setShowCantine(true);
          break;
        case "health":
          setShowHealth(true);
          break;
        case "medications":
          setShowMedications(true);
          break;
      }
    } else if (section.path) {
      navigate(section.path);
    }
  };

  // Composant modal adaptatif (Drawer sur mobile, Dialog sur desktop)
  const ResponsiveModal = ({ open, onOpenChange, title, icon: Icon, iconColor, children }) => {
    if (isMobile) {
      return (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="border-b">
              <DrawerTitle className="flex items-center gap-2">
                {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
                {title}
              </DrawerTitle>
            </DrawerHeader>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {children}
            </div>
          </DrawerContent>
        </Drawer>
      );
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
              {title}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            {children}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Gestion de classe</h2>
        <p className="text-slate-500 text-sm">
          Gérez vos élèves, l'appel et les registres de classe
        </p>
      </div>

      {/* Actions principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CLASS_SECTIONS.map((section) => (
          <Card
            key={section.id}
            data-testid={`class-${section.id}`}
            className={`cursor-pointer transition-all duration-200 border-2 ${section.lightColor} active:scale-[0.98]`}
            onClick={() => handleSectionClick(section)}
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`${section.color} p-2.5 sm:p-3 rounded-xl`}>
                    <section.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{section.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{section.description}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Section Registres */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Registres</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {REGISTRY_SECTIONS.map((section) => (
            <Card
              key={section.id}
              data-testid={`registry-${section.id}`}
              className={`cursor-pointer transition-all duration-200 border ${section.lightColor} active:scale-[0.98]`}
              onClick={() => handleSectionClick(section)}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className={`${section.color} p-2 rounded-lg`}>
                    <section.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-800 text-xs sm:text-sm">{section.title}</h4>
                    <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">{section.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Aperçu rapide */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Aperçu rapide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-slate-800">{stats.totalStudents}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Élèves</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-green-50 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-green-700">-</p>
              <p className="text-[10px] sm:text-xs text-green-600">Présents</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-amber-50 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-amber-700">{stats.cantineCount}</p>
              <p className="text-[10px] sm:text-xs text-amber-600">Cantine</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-rose-50 rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-rose-700">{stats.paiCount}</p>
              <p className="text-[10px] sm:text-xs text-rose-600">PAI / Allergies</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bouton ajout rapide */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-slate-800 mb-1">Ajouter un élève</h3>
              <p className="text-sm text-slate-500">
                Créer un nouveau profil avec code d'accès
              </p>
            </div>
            <Button
              onClick={() => navigate("/students")}
              className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modaux */}
      <ResponsiveModal
        open={showAttendance}
        onOpenChange={setShowAttendance}
        title="Module d'appel"
        icon={ClipboardList}
        iconColor="text-indigo-600"
      >
        <AttendanceModule onClose={() => setShowAttendance(false)} />
      </ResponsiveModal>

      <ResponsiveModal
        open={showCantine}
        onOpenChange={setShowCantine}
        title="Cantine"
        icon={Utensils}
        iconColor="text-amber-600"
      >
        <CantineModule onClose={() => setShowCantine(false)} />
      </ResponsiveModal>

      <ResponsiveModal
        open={showHealth}
        onOpenChange={setShowHealth}
        title="Santé et allergies"
        icon={Heart}
        iconColor="text-rose-600"
      >
        <HealthModule onClose={() => setShowHealth(false)} />
      </ResponsiveModal>

      <ResponsiveModal
        open={showMedications}
        onOpenChange={setShowMedications}
        title="Médicaments sorties"
        icon={Pill}
        iconColor="text-teal-600"
      >
        <MedicationsModule onClose={() => setShowMedications(false)} />
      </ResponsiveModal>
    </div>
  );
}
